import { useEffect, useMemo, useRef, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import { getExamReviewQuestionDetail, getReviewPageData } from 'utils/supabase';

// components

import CardNavPreguntas from "components/Custom/CardNavPreguntas";
import HeaderResultadosPreguntas from "components/Custom/HeaderResultadosPreguntas";
import PreguntaInfo from "components/Custom/PreguntaInfo";
import PreguntasBody from "components/Custom/PreguntasBody";

function normalizeStatus(status) {
  if (status === 'correct') return 'correct';
  if (status === 'incorrect') return 'incorrect';
  return 'unanswered';
}

export default function Preguntas() {
  const { examId, questionId } = useParams();
  const history = useHistory();
  const headerRef = useRef(null);
  const [reviewPageData, setReviewPageData] = useState(null);
  const [questionDetail, setQuestionDetail] = useState(null);
  const [activeFilter, setActiveFilter] = useState('all');

  useEffect(() => {
    async function fetchReviewPageData() {
      try {
        const { data, error } = await getReviewPageData(examId);
        if (error) {
          console.error('Error fetching review page data:', error);
        } else {
          setReviewPageData(data);
        }
      } catch (error) {
        console.error('Unexpected error fetching review page data:', error);
      }
    }

    fetchReviewPageData();
  }, [examId]);

  const navigatorItems = useMemo(() => {
    return (reviewPageData?.navigator || [])
      .map((item) => ({
        ...item,
        position: Number(item?.position),
        normalizedStatus: normalizeStatus(item?.status),
      }))
      .filter((item) => Number.isFinite(item.position) && item.position > 0 && item?.question_id)
      .sort((a, b) => a.position - b.position);
  }, [reviewPageData]);

  const effectiveQuestionId = useMemo(() => {
    if (questionId) return questionId;
    return reviewPageData?.first_question_id || navigatorItems[0]?.question_id || null;
  }, [questionId, reviewPageData, navigatorItems]);

  useEffect(() => {
    if (!headerRef.current) return;

    const nav = document.querySelector('nav');
    const navHeight = nav ? nav.getBoundingClientRect().height : 0;

    const rafId = window.requestAnimationFrame(() => {
      const headerBottom = window.scrollY + headerRef.current.getBoundingClientRect().bottom;
      const targetTop = Math.max(0, headerBottom - navHeight - 8);

      window.scrollTo({
        top: targetTop,
        behavior: 'smooth',
      });
    });

    return () => window.cancelAnimationFrame(rafId);
  }, [examId, effectiveQuestionId, activeFilter]);

  useEffect(() => {
    if (!examId || !effectiveQuestionId) {
      setQuestionDetail(null);
      return;
    }

    async function fetchQuestionDetail() {
      try {
        const { data, error } = await getExamReviewQuestionDetail(examId, effectiveQuestionId);
        if (error) {
          console.error('Error fetching question detail:', error);
          setQuestionDetail(null);
        } else {
          setQuestionDetail(Array.isArray(data) ? data[0] : data);
        }
      } catch (error) {
        console.error('Unexpected error fetching question detail:', error);
        setQuestionDetail(null);
      }
    }

    fetchQuestionDetail();
  }, [examId, effectiveQuestionId]);

  const currentQuestionPosition = useMemo(() => {
    if (!effectiveQuestionId) return null;
    const currentItem = navigatorItems.find((item) => item?.question_id === effectiveQuestionId);
    return currentItem?.position ?? null;
  }, [navigatorItems, effectiveQuestionId]);

  const navigationSource = useMemo(() => {
    if (activeFilter === 'all') return navigatorItems;
    return navigatorItems.filter((item) => item.normalizedStatus === activeFilter);
  }, [navigatorItems, activeFilter]);

  const previousQuestion = useMemo(() => {
    if (!effectiveQuestionId || !Number.isFinite(currentQuestionPosition)) return null;
    const candidates = navigationSource.filter((item) => item.position < currentQuestionPosition);
    return candidates.length > 0 ? candidates[candidates.length - 1] : null;
  }, [navigationSource, effectiveQuestionId, currentQuestionPosition]);

  const nextQuestion = useMemo(() => {
    if (!effectiveQuestionId || !Number.isFinite(currentQuestionPosition)) return null;
    return navigationSource.find((item) => item.position > currentQuestionPosition) || null;
  }, [navigationSource, effectiveQuestionId, currentQuestionPosition]);

  const questionFilters = {
    all: Number(reviewPageData?.filters?.all ?? navigatorItems.length ?? 0),
    correct: Number(reviewPageData?.filters?.correct ?? navigatorItems.filter((i) => i.normalizedStatus === 'correct').length),
    incorrect: Number(reviewPageData?.filters?.incorrect ?? navigatorItems.filter((i) => i.normalizedStatus === 'incorrect').length),
    unanswered: Number(reviewPageData?.filters?.unanswered ?? navigatorItems.filter((i) => i.normalizedStatus === 'unanswered').length),
  };

  const handleBackToResults = () => {
    history.push(`/admin/simulador/${examId}/resultados`);
  };

  const handleQuestionSelect = (item) => {
    if (!item?.question_id) return;
    history.push(`/admin/simulador/${examId}/preguntas/${item.question_id}`);
  };

  const handleFilterChange = (filter) => {
    setActiveFilter(filter);

    const source = filter === 'all'
      ? navigatorItems
      : navigatorItems.filter((item) => item.normalizedStatus === filter);

    if (source.length === 0) return;

    const currentExists = source.some((item) => item.question_id === effectiveQuestionId);
    if (!currentExists) {
      history.push(`/admin/simulador/${examId}/preguntas/${source[0].question_id}`);
    }
  };

  const handlePrevious = () => {
    if (!previousQuestion?.question_id) return;
    history.push(`/admin/simulador/${examId}/preguntas/${previousQuestion.question_id}`);
  };

  const handleNext = () => {
    if (!nextQuestion?.question_id) return;
    history.push(`/admin/simulador/${examId}/preguntas/${nextQuestion.question_id}`);
  };

  const handleViewDetail = () => {
    const sourceUrl = questionDetail?.question?.source_url || questionDetail?.clinical_case?.extra_data?.source_url;
    if (!sourceUrl) return;
    window.open(sourceUrl, '_blank', 'noopener,noreferrer');
  };

  return (
    <>
      <div className="flex flex-wrap mt-4">
        <div
          ref={headerRef}
          className="w-full mb-12 px-4"
        >
          <HeaderResultadosPreguntas
            resultsPayload={reviewPageData}
            onBack={handleBackToResults}
          />
        </div>

        <div className="w-full lg:w-8/12 mb-6 px-4" style={{ scrollMarginTop: '104px' }}>
          <PreguntasBody
            questionPayload={questionDetail}
            filters={questionFilters}
            activeFilter={activeFilter}
            onFilterChange={handleFilterChange}
            totalQuestions={questionFilters.all}
            onPrevious={handlePrevious}
            onNext={handleNext}
            onViewDetail={handleViewDetail}
            disablePrevious={!previousQuestion}
            disableNext={!nextQuestion}
          />
        </div>
        <div className="w-full lg:w-4/12 mb-6 px-4">
          <CardNavPreguntas
            resultsPayload={reviewPageData}
            currentQuestionPosition={currentQuestionPosition}
            onQuestionSelect={handleQuestionSelect}
          />

          <div className="mt-4">
            <PreguntaInfo
              reviewPayload={reviewPageData}
              questionPayload={questionDetail}
              currentQuestionId={effectiveQuestionId}
              currentQuestionPosition={currentQuestionPosition}
            />
          </div>
        </div>
      </div>
    </>
  );
}
